import ContentIllustration from '../../components/ui/ContentIllustration';
import SiteFooter from '../../components/layout/SiteFooter';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, X as XIcon, ArrowRight } from 'lucide-react';
import usePageTitle from '../../hooks/usePageTitle';

/**
 * /decouvrir
 *
 * Page de présentation honnête de MonQuiz pour les visiteurs qui arrivent
 * depuis la landing du livre (ou ailleurs). Modes principaux + tarif
 * explicite + reconnaissance des limites. Bouton final vers /inscription
 * en propageant le paramètre ?from=.
 *
 * Volontairement éditoriale : pas de "places limitées", pas de "promo",
 * pas de "essai gratuit limité dans le temps".
 */

const MODES = [
    {
        icon: 'book-open',
        title: 'Quiz par sous-sujet',
        desc: 'Tu choisis un sujet, tu fais le quiz en mode classique (correction à la fin) ou apprentissage (correction immédiate après chaque question avec explication rédigée).',
    },
    {
        icon: 'file-text',
        title: 'Quiz transversal',
        desc: 'Tirage aléatoire dans tout un scope, 5 à 50 questions, filtrable par matières et difficulté. Pour s’entraîner sur un mélange de matières.',
    },
    {
        icon: 'brain',
        title: 'Smart Quiz adaptatif',
        desc: 'L\'algorithme compose un quiz selon ton profil : un peu de faiblesses, un peu de révision, un peu de consolidation, un peu de nouveau. Tu choisis le dosage entre 4 sous-modes.',
    },
    {
        icon: 'target',
        title: 'Quiz sur erreurs',
        desc: 'Trois filtres : questions jamais réussies, questions instables (parfois juste, parfois faux), erreurs des 3 dernières sessions. Pour ne plus recommencer les mêmes pièges.',
    },
    {
        icon: 'calendar-clock',
        title: 'Révisions du jour',
        desc: 'Mode de répétition espacée individualisée. Chaque question a son propre calendrier selon ta performance. Tu te connectes, tu vois ce qui est dû aujourd\'hui, tu fais.',
    },
];

const PRICING = [
    { duree: '1 mois', prix: 'à partir de 3 €', note: 'Pour tester sur un concours' },
    { duree: '6 mois', prix: 'à partir de 15 €', note: 'Le plus choisi' },
    { duree: '12 mois', prix: 'à partir de 27 €', note: 'Pour un parcours complet' },
];

export default function Decouvrir() {
    usePageTitle('Découvrir MonQuiz');
    const [searchParams] = useSearchParams();
    const fromParam = searchParams.get('from');
    const registerHref = fromParam ? `/inscription?from=${encodeURIComponent(fromParam)}` : '/inscription';

    return (
        <div className="livre-page">
            <div className="livre-inner">

                {/* Hero */}
                <section className="livre-section livre-section--hero">
                    <h1 className="livre-title">
                        MonQuiz, <span className="livre-title-accent">en 3 minutes</span>
                    </h1>
                    <div className="livre-hero-text">
                        <p>
                            MonQuiz est une plateforme française d'entraînement aux concours à QCM. Banc de questions construit par concours, explication rédigée pour chaque réponse, suivi par tag et calendrier de révision espacée intégré.
                        </p>
                        <p style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
                            Cette page existe pour que tu décides en connaissance de cause si l'outil te correspond — pas pour te pousser à t'inscrire à tout prix.
                        </p>
                    </div>
                </section>

                {/* Modes principaux */}
                <section className="livre-section">
                    <h2 className="livre-section-title">5 modes d'entraînement</h2>
                    <p className="livre-section-sub">
                        Tous les modes utilisent le même banc de questions enrichi d'explications. Tu alternes selon le moment du parcours.
                    </p>
                    <div className="decouvrir-modes">
                        {MODES.map((m, i) => {

                            return (
                                <div key={i} className="decouvrir-mode">
                                    <div className="decouvrir-mode-icon content-mode-icon"><ContentIllustration name={m.icon} size={56} /></div>
                                    <div className="decouvrir-mode-body">
                                        <strong>{m.title}</strong>
                                        <p>{m.desc}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Ce que MonQuiz fait + ne fait pas */}
                <section className="livre-section">
                    <h2 className="livre-section-title">En toute transparence</h2>
                    <div className="decouvrir-honest">
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><CheckCircle size={16} /> Ce que MonQuiz fait bien</h3>
                            <ul>
                                <li>Banc QCM construit par concours, sans avoir à fabriquer le contenu</li>
                                <li>Explication rédigée et systématique pour chaque question</li>
                                <li>Calendrier de révision espacée par question (pas par paquet)</li>
                                <li>Suivi par tag — top 5 faiblesses + notions stale automatiquement</li>
                                <li>Accessible web sur ordinateur, tablette, mobile</li>
                            </ul>
                        </div>
                        <div className="decouvrir-honest-col decouvrir-honest-col--bad">
                            <h3><XIcon size={16} /> Ce qu'il ne fait pas</h3>
                            <ul>
                                <li>Pas de simulation chronométrée d'épreuve réelle (utilise les annales)</li>
                                <li>Pas un Anki personnalisable — contenu pré-construit</li>
                                <li>Limité aux concours français à QCM couverts</li>
                                <li>Pas hors-ligne (besoin de connexion internet)</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Tarif */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Tarif transparent</h2>
                    <p className="livre-section-sub">
                        Paiement unique par thématique, sans renouvellement automatique. Tu accèdes à plusieurs sujets gratuitement après inscription pour tester avant d'acheter.
                    </p>
                    <div className="decouvrir-pricing">
                        {PRICING.map((p, i) => (
                            <div key={i} className={`decouvrir-price-card ${i === 1 ? 'decouvrir-price-card--main' : ''}`}>
                                <div className="decouvrir-price-duree">{p.duree}</div>
                                <div className="decouvrir-price-amount">{p.prix}</div>
                                <div className="decouvrir-price-note">{p.note}</div>
                            </div>
                        ))}
                    </div>
                    <p className="decouvrir-pricing-note">
                        Le tarif exact dépend du concours visé (entre 3 et 28 €). Le détail apparaît au moment de l'achat sur la page de chaque thématique.
                    </p>
                </section>

                {/* Reco honnête */}
                <section className="livre-section livre-section--narrow">
                    <h2 className="livre-section-title">Comment décider</h2>
                    <ul className="decouvrir-decide">
                        <li><strong>Si tu débutes</strong> avec moins de 50 cartes, le papier suffit largement. Pas la peine de t'inscrire ici tout de suite.</li>
                        <li><strong>Si tu construis tes propres cartes</strong> et que tu vises plusieurs centaines de notions, Anki reste la référence historique.</li>
                        <li><strong>Si ton concours est dans moins de 8 semaines</strong>, les annales officielles passent avant toute plateforme.</li>
                        <li><strong>Si tu veux gagner du temps</strong> sur la fabrication des questions, profiter d'un calendrier de révision intégré et suivre tes erreurs sans tenir un journal manuel — alors une plateforme spécialisée comme MonQuiz a du sens.</li>
                    </ul>
                </section>

                {/* CTA final */}
                <section className="livre-section livre-section--cta">
                    <h2 className="livre-section-title">Prêt à essayer ?</h2>
                    <p className="livre-section-sub">
                        L'inscription est gratuite et te donne accès à plusieurs sujets de démonstration sur chaque thématique, sans carte bancaire. Tu décides ensuite si tu veux passer en accès complet.
                    </p>
                    <div className="decouvrir-cta-actions">
                        <Link to={registerHref} className="btn btn-primary decouvrir-cta-btn">
                            Créer mon compte gratuit <ArrowRight size={16} />
                        </Link>
                        <Link to="/connexion" className="btn btn-secondary">
                            J'ai déjà un compte
                        </Link>
                    </div>
                </section>

            </div>

            <SiteFooter />
        </div>
    );
}
